import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import {
  IconLoader,
  IconEdit,
  ButtonPseudo,
  ButtonSize,
  ToggleInput,
  PermissionEnum,
  ButtonSecondary,
  IconExpand,
  InputText,
  SidePanel,
  SidePanelType,
  ResponseError,
  responseErrorsResolver,
  ButtonPrimary,
  Currency,
  Checkbox,
  InputSize,
  Popup,
  SidePanelLoader,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetEmployeesTransactionsTypesQuery,
  useLazyGetEmployeesCASPQuery,
  useUpdateCaspMutation,
  useCreateCaspMutation,
  useAttachCurrenciesToCASPMutation,
  useUpdateCaspCurrenciesMutation,
  useLazyGetCASPItemQuery,
} from '../settings/store/services/transactions-types';
import { useLazyGetActiveCurrenciesQuery } from '../../store/dictionaries/service';
import { clearCASPSidePanel } from '../settings/store/slices/transactions-types';
import { CASP, CASPCurrency } from '../settings/types';

import cn from '../settings/styles.module.css';

const CaspCurrencies = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [caspName, setCaspName] = useState('');
  const [caspActive, setCaspActive] = useState(false);
  const [createCaspCurrencies, setCreateCaspCurrencies] = useState<string[]>(
    [],
  );
  const [selectedCaspItem, setSelectedCaspItem] = useState<CASP>();
  const [searchValue, setSearchValue] = useState('');
  const [createPopupVisible, setCreatePopupVisible] = useState(false);
  const [selectedCasp, setSelectedCasp] = useState('');
  const [globalCreateError, setGlobalCreateError] = useState('');
  const [currenciesList, setCurrenciesList] = useState<Currency[]>([]);
  const [selectedCaspCurrencies, setSelectedCaspCurrencies] = useState<
    { id: string; active: boolean; currency?: Currency; currencyId: string }[]
  >([]);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [createDirtyPopupVisible, setCreateDirtyPopupVisible] = useState(false);
  const [errorList, setErrorList] = useState('');

  const dispatch = useAppDispatch();

  const {
    settings: {
      transactionsTypes: { values: transactionsTypes, casp, caspItem },
    },
    administrationDictionaries: { activeCurrencies, activePermissions },
  } = useAppSelector((state) => state);

  const [getTransactionTypesList, { isLoading: transactionTypesIsLoading }] =
    useLazyGetEmployeesTransactionsTypesQuery();
  const [getActiveCurrencies, { isLoading: activeCurrenciesIsLoading }] =
    useLazyGetActiveCurrenciesQuery();
  const [getEmployeesCasp, { isLoading }] = useLazyGetEmployeesCASPQuery();
  const [updateCasp, { isLoading: requestInProgress, isSuccess }] =
    useUpdateCaspMutation();
  const [createCasp, { isLoading: createInProgress }] = useCreateCaspMutation();
  const [attachCurrenciesToCASP, { isLoading: attachInProgress }] =
    useAttachCurrenciesToCASPMutation();
  const [updateCaspCurrencies, { isLoading: updateCurrenciesInProgress }] =
    useUpdateCaspCurrenciesMutation();
  const [
    getCaspItem,
    { isLoading: caspIsLoading, isFetching: caspIsFetching },
  ] = useLazyGetCASPItemQuery();

  const handleClose = () => {
    setCreatePopupVisible(false);
    setCaspName('');
    setCaspActive(false);
    setGlobalCreateError('');
    setSearchValue('');
    setCreateCaspCurrencies([]);

    if (createDirtyPopupVisible) {
      setCreateDirtyPopupVisible(false);
    }
  };

  const handleCloseEditPanel = () => {
    setPanelIsOpen(false);
    setCaspName('');
    setCaspActive(false);
    setGlobalCreateError('');
    setSearchValue('');
    setCreateCaspCurrencies([]);
    setSelectedCaspItem(undefined);
    setSelectedCaspCurrencies([]);
    setSelectedCasp('');

    dispatch(clearCASPSidePanel());

    if (dirtyPopupVisible) {
      setDirtyPopupVisible(false);
    }
  };

  const onCreateCasp = async () => {
    setGlobalCreateError('');

    const response = (await createCasp({
      name: caspName,
      active: caspActive,
    })) as { error?: ResponseError; data: { data: CASP } };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setGlobalCreateError,
      });
    } else if (!response.error && createCaspCurrencies?.length) {
      const attachResponse = (await attachCurrenciesToCASP({
        caspId: response.data.data.id,
        currencies: createCaspCurrencies?.map((item) => ({
          currencyId: item,
          active: true,
        })),
      })) as { error: ResponseError };

      if (!attachResponse.error) {
        handleClose();
      } else {
        responseErrorsResolver({
          errors: attachResponse.error,
          setError: () => {},
          setGlobalError: setGlobalCreateError,
        });
      }
    } else if (!response.error && !createCaspCurrencies.length) {
      handleClose();
    }
  };

  const onSaveCasp = async () => {
    const nextSelectedCaspItem = { ...selectedCaspItem };

    setGlobalCreateError('');

    let updateCurrenciesAvailable = true;
    let addCurrenciesAvailable = true;

    if (
      selectedCaspItem?.name !== caspName ||
      selectedCaspItem.active !== caspActive
    ) {
      updateCurrenciesAvailable = false;
      addCurrenciesAvailable = false;

      const response = (await updateCasp({
        id: selectedCaspItem?.id as string,
        body: { name: caspName, active: caspActive },
      })) as { error: ResponseError; data?: { data: CASP } };

      if (!response.error) {
        nextSelectedCaspItem.name = caspName;
        nextSelectedCaspItem.active = caspActive;

        if (
          !isEqual(
            selectedCaspItem?.currencies?.map((item) => ({
              id: item.id,
              active: item.active,
            })),
            selectedCaspCurrencies?.map((item) => ({
              id: item.id,
              active: item.active,
            })),
          )
        ) {
          updateCurrenciesAvailable = true;
        } else {
          addCurrenciesAvailable = true;
        }
      } else if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError: () => {},
          setGlobalError: setGlobalCreateError,
        });
      }
    }

    if (
      updateCurrenciesAvailable &&
      !isEqual(
        selectedCaspItem?.currencies?.map((item) => ({
          id: item.id,
          active: item.active,
        })),
        selectedCaspCurrencies?.map((item) => ({
          id: item.id,
          active: item.active,
        })),
      )
    ) {
      addCurrenciesAvailable = false;

      const updatedCurrencies: { id: string; active: boolean }[] = [];

      selectedCaspCurrencies?.forEach((item) => {
        const defaultCaspCurrency = selectedCaspItem?.currencies?.find(
          (currency) => currency.id === item.id,
        );

        if (item.active !== defaultCaspCurrency?.active) {
          updatedCurrencies.push({ id: item.id, active: item.active });
        }
      });

      const updateCurrenciesResponse = (await updateCaspCurrencies({
        body: updatedCurrencies,
      })) as { error: ResponseError };

      if (updateCurrenciesResponse.error) {
        responseErrorsResolver({
          errors: updateCurrenciesResponse.error,
          setError: () => {},
          setGlobalError: setGlobalCreateError,
        });
      } else if (!updateCurrenciesResponse.error) {
        addCurrenciesAvailable = true;

        const nextSelectedCaspItemCurrencies =
          nextSelectedCaspItem?.currencies?.map((item) => {
            const result = { ...item };

            const currentCurrency = updatedCurrencies?.find(
              (currency) => currency.id === result.id,
            );

            if (currentCurrency) {
              result.active = currentCurrency.active;
            }

            return result;
          });

        nextSelectedCaspItem.currencies = nextSelectedCaspItemCurrencies;
      }
    }

    if (addCurrenciesAvailable && createCaspCurrencies?.length) {
      const attachResponse = (await attachCurrenciesToCASP({
        caspId: selectedCaspItem?.id as string,
        currencies: createCaspCurrencies?.map((item) => ({
          currencyId: item,
          active: true,
        })),
      })) as { error: ResponseError; data?: { data: CASPCurrency[] } };

      if (attachResponse.error) {
        responseErrorsResolver({
          errors: attachResponse.error,
          setError: () => {},
          setGlobalError: setGlobalCreateError,
        });
      } else {
        setCreateCaspCurrencies([]);

        const nextSelectedCaspCurrencies = (
          [] as {
            id: string;
            active: boolean;
            currency?: Currency;
            currencyId: string;
          }[]
        ).concat(selectedCaspCurrencies);

        attachResponse?.data?.data?.forEach((item) => {
          nextSelectedCaspCurrencies.push({
            id: item.id,
            active: item.active,
            currency: activeCurrencies?.find(
              (currency) => currency.id === item.currencyId,
            ),
            currencyId: item.currencyId,
          });
        });

        setSelectedCaspCurrencies(nextSelectedCaspCurrencies);

        let nextSelectedCaspItemCurrencies: CASPCurrency[] = [];

        if (nextSelectedCaspItem.currencies?.length) {
          nextSelectedCaspItemCurrencies = ([] as CASPCurrency[]).concat(
            nextSelectedCaspItem.currencies,
          );
        }

        attachResponse?.data?.data?.forEach((item) => {
          nextSelectedCaspItemCurrencies.push({
            ...item,
            currency: activeCurrencies?.find(
              (currency) => currency.id === item.currencyId,
            ) as Currency,
          });
        });

        nextSelectedCaspItem.currencies = nextSelectedCaspItemCurrencies;
      }
    }

    setSelectedCaspItem(nextSelectedCaspItem as CASP);
  };

  const onChangeCasp = async ({
    value,
    id,
  }: {
    value: boolean;
    id: string;
  }) => {
    const currentCasp = casp?.find((item) => item.id === id);
    setSelectedCasp(id);

    if (currentCasp) {
      const body: { name: string; active: boolean } = {
        name: currentCasp?.name,
        active: value,
      };

      await updateCasp({ id, body });
    }
  };

  const onChangeSelectedCaspCurrencies = ({
    id,
    active,
  }: {
    id: string;
    active: boolean;
  }) => {
    const nextSelectedCaspCurrencies = selectedCaspCurrencies?.map((item) => {
      const result = { ...item };

      if (result.id === id) {
        result.active = active;
      }

      return result;
    });

    setSelectedCaspCurrencies(nextSelectedCaspCurrencies);
  };

  const onSearch = (text: string) => {
    setSearchValue(text);

    if (text) {
      const nextCurrencies = activeCurrencies?.filter((item) =>
        item.name.toLocaleLowerCase().includes(text.toLocaleLowerCase()),
      );

      setCurrenciesList(nextCurrencies as Currency[]);
    } else {
      setCurrenciesList(activeCurrencies as Currency[]);
    }
  };

  const getEmployeesCaspAction = useCallback(async () => {
    const response = (await getEmployeesCasp()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({ error: response?.error, setErrorList });
    }
  }, []);

  useEffect(() => {
    if (isSuccess && !selectedCaspItem) {
      setSelectedCasp('');
    }
  }, [isSuccess, selectedCaspItem]);

  useEffect(() => {
    if (!transactionsTypes) {
      getTransactionTypesList();
    }

    if (!activeCurrencies) {
      getActiveCurrencies();
    }
  }, [transactionsTypes, activeCurrencies]);

  useEffect(() => {
    if (activeCurrencies && (panelIsOpen || createPopupVisible)) {
      setCurrenciesList(activeCurrencies);
    }
  }, [activeCurrencies, panelIsOpen, createPopupVisible]);

  useEffect(() => {
    if (!casp) {
      getEmployeesCaspAction();
    }
  }, [casp]);

  useEffect(() => {
    if (caspItem) {
      setCaspName(caspItem.name);
      setCaspActive(caspItem.active);
      setSelectedCaspCurrencies(
        caspItem?.currencies?.map((item) => ({
          id: item.id,
          active: item.active,
          currency: item.currency,
          currencyId: item.currencyId,
        })),
      );
      setSelectedCaspItem(caspItem);
    }
  }, [caspItem]);

  useEffect(() => {
    let title = 'Transaction Settings: CASP Currency';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  let isDirty = false;

  if (
    !isEqual(
      { name: caspName, active: caspActive },
      { name: selectedCaspItem?.name, active: selectedCaspItem?.active },
    ) ||
    createCaspCurrencies?.length ||
    !isEqual(
      selectedCaspItem?.currencies?.map((item) => ({
        id: item.id,
        active: item.active,
      })),
      selectedCaspCurrencies?.map((item) => ({
        id: item.id,
        active: item.active,
      })),
    )
  ) {
    isDirty = true;
  }

  if (transactionTypesIsLoading || activeCurrenciesIsLoading || isLoading) {
    content = (
      <div className={cn.beneficiariesSettingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    let caspPanelContent;

    if (caspIsLoading || caspIsFetching) {
      caspPanelContent = <SidePanelLoader />;
    } else {
      caspPanelContent = (
        <div className={cn.createCaspForm}>
          <div className={cn.formGroup}>
            <ToggleInput
              name="casp-edit-available"
              checked={caspActive}
              title="Available"
              onChange={(e) => setCaspActive(e.target.checked)}
              visualClassName={cn.activeCaspToggle}
              disabled={
                !activePermissions?.includes(PermissionEnum.CASP_UPDATE_ACTION)
              }
            />
          </div>
          <div className={cn.formGroup}>
            <InputText
              name="caspName"
              value={caspName}
              placeholder="CASP name..."
              onChange={(e) => setCaspName(e.target.value)}
              disabled={
                !activePermissions?.includes(PermissionEnum.CASP_UPDATE_ACTION)
              }
            />
          </div>
          <div className={cn.availableCurrenciesWrapper}>
            <div className={cn.createCaspFormTitle}>Available Currencies</div>
            {selectedCaspCurrencies?.map((item) => {
              let title;

              if (item?.currency) {
                title = `${item.currency.name} (${item.currency.description})`;
              } else {
                const currentCurrency = activeCurrencies?.find(
                  (currency) => currency.id === item?.currencyId,
                );

                title = `${currentCurrency?.name} (${currentCurrency?.description})`;
              }
              return (
                <div className={cn.selectedCaspCurrencyWrapper} key={item.id}>
                  <ToggleInput
                    name={`casp-${item.id}-currency`}
                    checked={item.active}
                    title={title}
                    onChange={(e) =>
                      onChangeSelectedCaspCurrencies({
                        id: item.id,
                        active: e.target.checked,
                      })
                    }
                    visualClassName={cn.activeCaspToggle}
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.CASP_EDIT_STATUS_ACTION,
                      )
                    }
                  />
                </div>
              );
            })}
          </div>

          {activePermissions?.includes(PermissionEnum.CASP_UPDATE_ACTION) &&
            (currenciesList?.length !== selectedCaspCurrencies?.length ||
              !selectedCaspCurrencies.length) && (
              <>
                <InputText
                  name="search"
                  placeholder="Currency name"
                  value={searchValue}
                  labelClassName={cn.searchInput}
                  onChange={(e) => onSearch(e.target.value)}
                  size={InputSize.SMALL}
                />
                {!currenciesList.length && (
                  <div className={cn.emptyCurrencies}>No currencies yet</div>
                )}
                {currenciesList
                  ?.filter((item) =>
                    selectedCaspCurrencies?.every(
                      (currency) => item.id !== currency?.currency?.id,
                    ),
                  )
                  ?.map((item) => (
                    <div className={cn.currencyCheckbox} key={item.id}>
                      <Checkbox
                        name={`checkbox-${item.id}`}
                        checked={Boolean(
                          createCaspCurrencies?.find(
                            (currency) => currency === item.id,
                          ),
                        )}
                        type="checkbox"
                        title={item.name}
                        onChange={(e) => {
                          let nextCreateCaspCurrencies = (
                            [] as string[]
                          ).concat(createCaspCurrencies);

                          if (e.target.checked) {
                            nextCreateCaspCurrencies?.push(item.id);
                          } else {
                            nextCreateCaspCurrencies =
                              nextCreateCaspCurrencies?.filter(
                                (currency) => currency !== item.id,
                              );
                          }

                          setCreateCaspCurrencies(nextCreateCaspCurrencies);
                        }}
                      />
                      <div className={cn.currencyDescription}>
                        {item.description}
                      </div>
                    </div>
                  ))}
              </>
            )}
        </div>
      );
    }

    content = (
      <div className={cn.beneficiariesSettingsContent}>
        {!errorList && (
          <>
            <div className={cn.beneficiariesSettingsBlock}>
              <div className={cn.beneficiariesSettingsTitle}>
                Availability of CASP
                {activePermissions?.includes(
                  PermissionEnum.CASP_CREATE_ACTION,
                ) && (
                  <ButtonSecondary
                    className={`button-white ${cn.createCasp}`}
                    text="Create CASP"
                    size={ButtonSize.SMALL}
                    icon={<IconExpand className={cn.createCaspIcon} />}
                    onClick={() => setCreatePopupVisible(true)}
                  />
                )}
              </div>
            </div>
            {casp?.map((item) => (
              <div className={cn.casp} key={item.id}>
                <div className={cn.caspName}>{item.name}</div>
                <div className={cn.caspBlock}>
                  <div className={cn.toggleWrapper}>
                    <ToggleInput
                      name={`${item?.id}-enabled`}
                      checked={item?.active}
                      title="Available"
                      className={`${cn.statusToggle} ${
                        item?.active ? cn.checked : ''
                      }`}
                      onChange={(e) => {
                        onChangeCasp({ value: e.target.checked, id: item.id });
                      }}
                      visualClassName={cn.statusToggleVisual}
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.CASP_UPDATE_ACTION,
                        ) ||
                        (requestInProgress && selectedCasp === item.id)
                      }
                    />
                  </div>
                  <div className={cn.caspBlockContent}>
                    <div className={cn.caspBlockTop}>
                      <div className={cn.title}>Available currencies</div>
                      {(activePermissions?.includes(
                        PermissionEnum.CASP_EDIT_STATUS_ACTION,
                      ) ||
                        activePermissions?.includes(
                          PermissionEnum.CASP_UPDATE_ACTION,
                        )) && (
                        <ButtonPseudo
                          className={`button-white ${cn.editButton}`}
                          size={ButtonSize.SMALL}
                          icon={<IconEdit width={16} height={16} />}
                          onClick={() => {
                            setPanelIsOpen(true);
                            getCaspItem(item?.id);
                          }}
                          text="Edit"
                        />
                      )}
                    </div>
                    <div className={cn.currencies}>
                      {item?.currencies
                        ?.filter((currency) => currency.active)
                        ?.map((currency: any) => (
                          <div className={cn.caspCurrency} key={currency.id}>
                            {currency.currency?.name
                              ? currency?.currency.name
                              : activeCurrencies?.find(
                                  (activeCurrency) =>
                                    activeCurrency?.id === currency.currencyId,
                                )?.name}
                          </div>
                        ))}
                      {!item.currencies?.filter((currency) => currency?.active)
                        ?.length && (
                        <div className={cn.emptyAvailableCurrencies}>
                          No available currencies yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
        <SidePanel
          title={selectedCaspItem?.name as string}
          isOpen={panelIsOpen}
          type={SidePanelType.AML}
          onClose={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              handleCloseEditPanel();
            }
          }}
          sidepanelExpandedKey="casp"
          errorSidepanel={globalCreateError}
          footer={
            <>
              <ButtonPrimary
                text={
                  requestInProgress ||
                  updateCurrenciesInProgress ||
                  attachInProgress ||
                  caspIsLoading ||
                  caspIsFetching
                    ? 'Loading...'
                    : 'Save'
                }
                disabled={
                  !isDirty ||
                  requestInProgress ||
                  updateCurrenciesInProgress ||
                  attachInProgress ||
                  caspIsLoading ||
                  caspIsFetching
                }
                onClick={onSaveCasp}
                bold
              />
              <ButtonPseudo
                text="Close"
                onClick={() => {
                  if (isDirty) {
                    setDirtyPopupVisible(true);
                  } else {
                    handleCloseEditPanel();
                  }
                }}
                bold
              />
            </>
          }
          closeOnClickOutside
        >
          {caspPanelContent}
        </SidePanel>
        <SidePanel
          title="Add new CASP"
          isOpen={createPopupVisible}
          type={SidePanelType.AML}
          onClose={() => {
            if (caspName || createCaspCurrencies?.length) {
              setCreateDirtyPopupVisible(true);
            } else {
              handleClose();
            }
          }}
          sidepanelExpandedKey="casp"
          errorSidepanel={globalCreateError}
          footer={
            <>
              <ButtonPrimary
                text={
                  createInProgress || attachInProgress
                    ? 'Loading...'
                    : 'Add CASP'
                }
                disabled={!caspName || createInProgress || attachInProgress}
                onClick={onCreateCasp}
                bold
              />
              <ButtonPseudo
                text="Close"
                onClick={() => {
                  if (caspName || createCaspCurrencies?.length) {
                    setCreateDirtyPopupVisible(true);
                  } else {
                    handleClose();
                  }
                }}
                bold
              />
            </>
          }
          closeOnClickOutside
        >
          <div className={cn.createCaspForm}>
            <div className={cn.formGroup}>
              <ToggleInput
                name="casp-available"
                checked={caspActive}
                title="Available"
                onChange={(e) => setCaspActive(e.target.checked)}
                visualClassName={cn.activeCaspToggle}
              />
            </div>
            <div className={cn.formGroup}>
              <InputText
                name="caspName"
                value={caspName}
                placeholder="CASP name..."
                onChange={(e) => setCaspName(e.target.value)}
              />
            </div>
            {activePermissions?.includes(PermissionEnum.CASP_UPDATE_ACTION) && (
              <>
                <div className={cn.createCaspFormTitle}>Currencies</div>
                <InputText
                  name="search"
                  placeholder="Currency name"
                  value={searchValue}
                  labelClassName={cn.searchInput}
                  onChange={(e) => onSearch(e.target.value)}
                  size={InputSize.SMALL}
                />
                {currenciesList?.map((item) => (
                  <div className={cn.currencyCheckbox} key={item.id}>
                    <Checkbox
                      name={`checkbox-${item.id}`}
                      checked={Boolean(
                        createCaspCurrencies?.find(
                          (currency) => currency === item.id,
                        ),
                      )}
                      type="checkbox"
                      title={item.name}
                      onChange={(e) => {
                        let nextCreateCaspCurrencies = ([] as string[]).concat(
                          createCaspCurrencies,
                        );

                        if (e.target.checked) {
                          nextCreateCaspCurrencies?.push(item.id);
                        } else {
                          nextCreateCaspCurrencies =
                            nextCreateCaspCurrencies?.filter(
                              (currency) => currency !== item.id,
                            );
                        }

                        setCreateCaspCurrencies(nextCreateCaspCurrencies);
                      }}
                    />
                    <div className={cn.currencyDescription}>
                      {item.description}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </SidePanel>
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={handleCloseEditPanel}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setCreateDirtyPopupVisible(false)}
          onOkClick={handleClose}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={createDirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <div id="dirty-popup" />
      </div>
    );
  }

  return <div className={cn.beneficiariesSettingsWrapper}>{content}</div>;
};

export default CaspCurrencies;
